import { useEffect, useState } from "react";
import axios from "axios";
import { AdminLayout } from "../components/AdminLayout";
import { getAuthHeader } from "../context/AuthContext";

import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCurrency } from "@/utils/currency";
import {
  Loader2,
  Search,
  Phone,
  MapPin,
  Clock3,
  Bike,
  PackageCheck,
  ShoppingBag,
  ChefHat
} from "lucide-react";

import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// -----------------------------
// Helper formatter
// -----------------------------
const formatStatus = (value) => {
  if (!value) return "Pending";

  return value
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function OnlineOrdersManagementPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [drivers, setDrivers] = useState([]);
  const [driverLoading, setDriverLoading] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDriverModal, setOpenDriverModal] = useState(false);

  const [assigning, setAssigning] = useState("");
  const [quoteLoading, setQuoteLoading] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [restaurantNo, setRestaurantNo] = useState("");
  const [restaurantAddress, setRestaurantAddress] = useState("");
  const [assigningUber, setAssigningUber] = useState("");
  useEffect(() => {
    fetchOrders();
  }, [page]);
  // Fetch restaurant ID
  useEffect(() => {
    const fetchRestaurantId = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: getAuthHeader(),
        });
        const restaurantName = response.data.restaurant_name;
        setRestaurantName(restaurantName);
        const restaurantNo = response.data.restaurant_phone;
        setRestaurantNo(restaurantNo);
        const restaurantAddress = response.data.restaurant_address;
        setRestaurantAddress(restaurantAddress);
      } catch (error) {
        console.error("Error fetching restaurant ID:", error);
      }
    };
    fetchRestaurantId();
  }, []);
  const fetchOrders = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API}/delivery/orders`, {
        headers: getAuthHeader(),
        params: {
          page,
          limit: 20,
          search,
        },
      });

      setOrders(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load online orders");
    } finally {
      setLoading(false);
    }
  };
const getUberQuote = async (order) => {
  try {
    setQuoteLoading(order.id);

    const response = await axios.post(
      `${API}/delivery/uber/quote`,
      {
        pickup_address: restaurantAddress,
        dropoff_address: order.customer_address,

        pickup_name: restaurantName,
        pickup_phone_number: restaurantNo,

        dropoff_name: order.customer_name,
        dropoff_phone_number: order.customer_phone,
      },
      {
        headers: getAuthHeader(),
      }
    );

    const quoteData = response.data.data;

    // save quote into order
    await axios.put(
      `${API}/orders/${order.id}/quote`,
      {
        delivery_quote: quoteData,
      },
      {
        headers: getAuthHeader(),
      }
    );

    toast.success("Uber quote generated");

    fetchOrders();

  } catch (error) {

    toast.error(
      error?.response?.data?.detail?.message ||
      "Failed to get Uber quote"
    );

  } finally {
    setQuoteLoading("");
  }
};
  const fetchDrivers = async () => {
    try {
      setDriverLoading(true);

      const response = await axios.get(`${API}/delivery/staff`, {
        headers: getAuthHeader(),
      });

      setDrivers(response.data.data || []);
    } catch (error) {
      toast.error("Failed to load delivery staff");
    } finally {
      setDriverLoading(false);
    }
  };

  const openAssignModal = async (order) => {
    setSelectedOrder(order);
    setOpenDriverModal(true);
    await fetchDrivers();
  };

  const assignDriver = async (driverId) => {
    try {
      setAssigning(driverId);

      await axios.post(
        `${API}/delivery/assign-own`,
        {
          order_id: selectedOrder.id,
          driver_id: driverId,
        },
        {
          headers: getAuthHeader(),
        }
      );

      toast.success("Driver assigned successfully");
      setOpenDriverModal(false);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to assign driver");
    } finally {
      setAssigning("");
    }
  };

const assignUber = async (order) => {
  try {

    setAssigningUber(order.id);

    const response = await axios.post(
      `${API}/delivery/uber/create`,
      {
        order_id: order.id
      },
      {
        headers: getAuthHeader()
      }
    );

    toast.success("Uber delivery created");

    await fetchOrders();

  } catch (error) {

    toast.error(
      error?.response?.data?.detail ||
      "Failed to create Uber delivery"
    );

  } finally {

    setAssigningUber("");
  }
};

  const getStatusColor = (status) => {
    switch (status) {
      case "ready":
        return "bg-green-100 text-green-700";
      case "in_progress":
      case "preparing":
        return "bg-yellow-100 text-yellow-700";
      case "pending":
        return "bg-gray-100 text-gray-700";
      case "delivered":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 bg-slate-50 min-h-screen">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <h1 className="text-4xl font-bold text-slate-800">
              Online Order Management
            </h1>
            <p className="text-slate-500 mt-2">
              Manage online orders and assign delivery
            </p>
          </div>

          <div className="flex gap-3">
            <div className="relative w-[320px]">
              <Search
                size={18}
                className="absolute left-3 top-3 text-slate-400"
              />
              <Input
                className="pl-10 bg-white"
                placeholder="Search order / customer / phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <Button onClick={fetchOrders}>Search</Button>
          </div>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Total Orders</p>
              <h2 className="text-3xl font-bold mt-2">{orders.length}</h2>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Pending Dispatch</p>
              <h2 className="text-3xl font-bold mt-2 text-orange-600">
                {
                  orders.filter(
                    (o) => o.delivery_status === "pending"
                  ).length
                }
              </h2>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Out For Delivery</p>
              <h2 className="text-3xl font-bold mt-2 text-blue-600">
                {
                  orders.filter(
                    (o) => o.delivery_status === "out_for_delivery" || o.delivery_status === "assigned"
                  ).length
                }
              </h2>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-slate-500">Delivered</p>
              <h2 className="text-3xl font-bold mt-2 text-green-600">
                {
                  orders.filter(
                    (o) => o.delivery_status === "delivered"
                  ).length
                }
              </h2>
            </CardContent>
          </Card>
        </div>
        {/* Orders */}
        {loading ? (
          <div className="flex items-center gap-3 text-slate-600">
            <Loader2 className="animate-spin" size={18} />
            Loading orders...
          </div>
        ) : (
          <div className="space-y-5">
            {orders.map((order) => (
              <Card
                key={order.id}
                className="rounded-2xl border-0 shadow-sm"
              >
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-3 gap-6">
                    {/* Left */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <ShoppingBag size={18} />
                        <h2 className="font-bold text-lg">
                          #{order.id.slice(0, 8)}
                        </h2>

                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            order || order.order_status
                          )}`}
                        >
                          {order.order_status || "pending"}
                        </span>
                      </div>

                      <p className="font-semibold">
                        {order.customer_name}
                      </p>

                      <div className="text-sm text-slate-500 mt-2 space-y-2">
                        <div className="flex gap-2">
                          <Phone size={14} />
                          {order.customer_phone}
                        </div>

                        <div className="flex gap-2">
                          <MapPin size={14} />
                          {order.customer_address}
                        </div>

                        <div className="flex gap-2">
                          <Clock3 size={14} />
                          {new Date(
                            order.created_at
                          ).toLocaleString()}
                        </div>
                          <div className="flex gap-2">
                            <ChefHat size={14} />
                            {(order.kitchen_status === "ready" && "Ready for Delivery")||(order.kitchen_status === "pending" && "Being Prepared")||"Kitchen Pending"}
                          </div>
                      </div>
                    </div>

                    {/* Center */}
                    <div>
                      <p className="font-semibold mb-3">Items</p>

                      <div className="space-y-2 text-sm">
                        {order.items?.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between border-b pb-2"
                          >
                            <span>
                              {item.quantity} x {item.item_name}
                            </span>
                            <span>{formatCurrency(item.price)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 font-bold text-lg">
                        {formatCurrency(order.grand_total)}
                      </div>
                    </div>

                    {/* Right */}
                    <div>
                      <p className="font-semibold mb-3">
                        Delivery Action
                      </p>

                      <div className="space-y-3">

                        {/* PICKUP ORDER */}
                        {order.delivery_type === "pickup" && (

                          <div className="border rounded-xl p-4 bg-slate-50 text-center">
                            <PackageCheck className="mx-auto mb-2 text-green-600" size={22} />

                            <p className="font-semibold">
                              Pickup Order
                            </p>

                            <p className="text-sm text-slate-500 mt-1">
                              Customer will collect from restaurant
                            </p>
                          </div>

                        )}

                        {/* INTERNAL DELIVERY */}
                        {order.delivery_type === "delivery" &&
                          order.delivery_provider === "internal" && (

                          <>
                            {/* Already Assigned */}
                            {order.delivery_status &&
                            order.delivery_status !== "pending" ? (

                              <Button
                                disabled
                                className="w-full opacity-70 cursor-not-allowed"
                              >
                                <Bike className="mr-2 h-4 w-4" />

                                Rider Assigned
                              </Button>

                            ) : order.kitchen_status !== "ready" ? (

                              <Button
                                disabled
                                className="w-full opacity-70 cursor-not-allowed"
                              >
                                <ChefHat className="mr-2 h-4 w-4" />

                                Being Prepared
                              </Button>

                            ) : (

                              <Button
                                className="w-full"
                                onClick={() => openAssignModal(order)}
                              >
                                <Bike className="mr-2 h-4 w-4" />

                                Assign Own Staff
                              </Button>

                            )}
                          </>

                        )}

                        {/* UBER DELIVERY */}
                        {order.delivery_type === "delivery" &&
                          order.delivery_provider === "uber" && (

                          <div className="space-y-3">

                            <div className="border rounded-xl p-4 bg-blue-50">

                              <div className="flex items-center gap-2 mb-2">
                                <Bike className="text-blue-600" size={18} />

                                <span className="font-semibold text-blue-700">
                                  Uber Delivery
                                </span>
                              </div>

                              <p className="text-sm text-slate-600">
                                Delivery handled automatically by Uber Direct
                              </p>

                            </div>

                            {order.uber_tracking_url && (

                              <a
                                href={order.uber_tracking_url}
                                target="_blank"
                                rel="noreferrer"
                                className="block"
                              >

                                <Button
                                  variant="outline"
                                  className="w-full"
                                >
                                  Track Uber Delivery
                                </Button>

                              </a>

                            )}

                          </div>

                        )}

                        {/* Delivery Status */}
                        <div className="text-sm text-slate-500 pt-2">
                          Delivery Status:{" "}
                          <span className="font-semibold text-slate-800">
                            {formatStatus(order.delivery_status)}
                          </span>
                        </div>

                        {/* Driver */}
                        {order.assigned_driver_name && (
                          <div className="text-sm text-green-600 font-medium">
                            Driver: {order.assigned_driver_name}
                          </div>
                        )}

                        {/* Payment Method */}
                        <div className="text-sm text-slate-500">
                          Payment Method:{" "}
                          <span className="font-semibold text-slate-800">
                            {order.payment_method
                              ? formatStatus(order.payment_method)
                              : "Online"}
                          </span>
                        </div>

                        {/* Payment Status */}
                        <div className="text-sm text-slate-500">
                          Payment Status:{" "}
                          <span
                            className={`font-semibold ${
                              order.payment_status === "paid"
                                ? "text-green-600"
                                : "text-orange-600"
                            }`}
                          >
                            {formatStatus(order.payment_status)}
                          </span>
                        </div>

                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {orders.length === 0 && (
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardContent className="p-10 text-center text-slate-500">
                  No online orders found
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Driver Modal */}
        <Dialog
          open={openDriverModal}
          onOpenChange={setOpenDriverModal}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Assign Delivery Staff
              </DialogTitle>
            </DialogHeader>

            {driverLoading ? (
              <div className="flex gap-2 items-center py-6">
                <Loader2
                  className="animate-spin"
                  size={18}
                />
                Loading staff...
              </div>
            ) : (
              <div className="space-y-3">
                {drivers.map((driver) => (
                  <div
                    key={driver.id}
                    className="border rounded-xl p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-semibold">
                        {driver.name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {driver.phone}
                      </p>
                    </div>

                    <Button
                      disabled={assigning === driver.id}
                      onClick={() =>
                        assignDriver(driver.id)
                      }
                    >
                      {assigning === driver.id ? (
                        <Loader2 className="animate-spin h-4 w-4" />
                      ) : (
                        "Assign"
                      )}
                    </Button>
                  </div>
                ))}

                {drivers.length === 0 && (
                  <p className="text-center text-slate-500 py-6">
                    No delivery staff found
                  </p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}